import {
  AppleLogo,
  Copy,
  GoogleLogo,
  NavigationArrow,
} from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { copyText } from '@/features/discovery/clipboard'
import { Sheet } from '@/features/discovery/components/sheet'
import { formatCoordinates, mapHandoffUrls } from './handoff-links'

interface MapHandoffSheetProps {
  open: boolean
  onClose: () => void
  title: string
  latitude: number | null
  longitude: number | null
}

/**
 * D-015 map handoff: explicit external navigation only. We use HTTPS universal
 * links, so installed apps open when the platform supports them and the
 * browser map is the fallback. There is no installed-app detection.
 */
export function MapHandoffSheet({
  open,
  onClose,
  title,
  latitude,
  longitude,
}: MapHandoffSheetProps) {
  const { t } = useTranslation()
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>(
    'idle',
  )
  const hasCoordinates = latitude !== null && longitude !== null
  const urls = hasCoordinates
    ? mapHandoffUrls({ latitude, longitude, label: title })
    : null
  const coordinates = hasCoordinates
    ? formatCoordinates(latitude, longitude)
    : ''

  async function handleCopy() {
    if (!coordinates) return
    const copied = await copyText(coordinates)
    setCopyStatus(copied ? 'copied' : 'failed')
  }

  return (
    <Sheet open={open} onClose={onClose} titleKey="map.handoff.title">
      <p className="mb-4 text-sm text-muted">{t('map.handoff.body')}</p>
      {!hasCoordinates ? (
        <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
          {t('map.handoff.noCoordinates')}
        </p>
      ) : (
        <div className="space-y-2">
          <a
            href={urls?.apple}
            target="_blank"
            rel="noreferrer"
            className="handoff-link"
          >
            <span className="handoff-link__icon">
              <AppleLogo size={22} weight="bold" aria-hidden="true" />
            </span>
            {t('map.handoff.apple')}
          </a>
          <a
            href={urls?.google}
            target="_blank"
            rel="noreferrer"
            className="handoff-link"
          >
            <span className="handoff-link__icon">
              <GoogleLogo size={22} weight="bold" aria-hidden="true" />
            </span>
            {t('map.handoff.google')}
          </a>
          <a
            href={urls?.waze}
            target="_blank"
            rel="noreferrer"
            className="handoff-link"
          >
            <span className="handoff-link__icon">
              <NavigationArrow size={22} weight="bold" aria-hidden="true" />
            </span>
            {t('map.handoff.waze')}
          </a>
          <button
            type="button"
            className="handoff-link w-full"
            onClick={() => void handleCopy()}
          >
            <span className="handoff-link__icon">
              <Copy size={22} weight="bold" aria-hidden="true" />
            </span>
            {t('map.handoff.copy')}
            <span className="ml-auto font-mono text-xs font-normal text-muted">
              {coordinates}
            </span>
          </button>
          {copyStatus !== 'idle' ? (
            <p
              role="status"
              className={`text-sm ${copyStatus === 'failed' ? 'text-danger' : 'text-teal'}`}
            >
              {copyStatus === 'copied'
                ? t('map.handoff.copied')
                : t('map.handoff.copyFailed')}
            </p>
          ) : null}
        </div>
      )}
    </Sheet>
  )
}
